import * as EndPoints from "./FwaasUriEndPoints";
import { ApplicationConfigManager } from "../types";
import {
    CreateLinkAccountRequest, DeleteFirewallRequest, DeleteUserRoleMappingRequest, DescribeLogProfileRequest,
    DescribeNetworkMonitoringRequest,
    DescribeTenantSettingsRequest,
    GetFederatedAccessRequest,
    GetSupportRequest,
    ListFirewallRequest, ListLinkAccountRequest, ListRulesRequest, ListRuleStacksRequest,
    ListSubscriptionRequest, ListUserRoleMappingRequest, ListXAccountRequest, UpdateFirewallRequest, UpdateLogProfileRequest,
    UpdateNetworkMonitoringRequest,
    UpdateSupportRequest,
    UpdateTenantSettingsRequest,
    UpdateUserRoleMappingRequest
} from "../interfaces/interfaces";
import configuration from "../config.json"
import EventEmitter from "../eventEmitter";
import { authProvider } from "../authProvider";
import { payloadToLowercaseParams } from "./utils";
import { isEmpty } from "lodash";


export const prepareHeaders = async (headers: Headers) => {
    await ApplicationConfigManager.getInstance().ensureSession();
    const token = localStorage.getItem('idToken');
    const xApiKey = ApplicationConfigManager.getInstance().getXApiKey();

    // Only set Authorization if not already present
    if (!headers.has('Authorization')) {
        headers.set('Authorization', token || '');
    }

    // Only set x-api-key if not already present
    if (!headers.has('x-api-key')) {
        headers.set('x-api-key', xApiKey);
    }

    // Only set Content-Type if not already present
    if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }

    return headers;
};

export const buildError = (response: any, exception: any, options: any = {}) => {
    let errInfo;
    if ((response && (response.status === 401)) || (exception && (exception?.body?.status === 401))) {
        console.log("Access Denied with 401, logout triggered :" + response.status);
        ApplicationConfigManager.getInstance().clearSession();
        EventEmitter.emit("logoutUser", () => { });
        authProvider.logout(null);
        errInfo = {
            code: response.status,
            error: response?.json?.message || "Session expired, please login again"
        };
    } else {
        if (response) {
            errInfo = {
                code: response.status,
                error: response?.data?.Reason || response?.data?.message
                    || response?.data?.ResponseStatus?.Reason || "Something went wrong!"
            };
        } else {
            let message = exception?.body?.ResponseStatus?.Reason || exception?.body?.message || exception?.message || exception?.error || exception?.json?.message;
            errInfo = {
                code: 0,
                error: message || exception || "Something went wrong!"
            };
        }
    }

    return { ...errInfo, ...options };
};

const resourceApiVersionMap: { [key in EndPoints.RESOURCE]?: string } = {
    [EndPoints.RESOURCE.LINKS]: 'v2',
    [EndPoints.RESOURCE.CLOUDMANAGER]: 'v2',
    [EndPoints.RESOURCE.VALIDATE_SCM_CODE]: 'v2',
    [EndPoints.RESOURCE.SCM_BILLING_ASSOCIATION]: 'v2',
    [EndPoints.RESOURCE.SUBSCRIPTION]: (import.meta.env.VITE_MICROSUBSCRIPTIONS === 'true') ? 'v2' : 'v1',
    [EndPoints.RESOURCE.ACCOUNT]: (import.meta.env.VITE_MICROSUBSCRIPTIONS === 'true') ? 'v2' : 'v1',
    [EndPoints.RESOURCE.NETWORK_MONITORING]: 'v2',
    [EndPoints.RESOURCE.FIREWALL]: 'v2',
    [EndPoints.RESOURCE.SETTINGS]: 'v2',
    [EndPoints.RESOURCE.TENANT]: 'v2',
};

const MP_REGION = () => ApplicationConfigManager.getInstance().getSupportedRegions()[0].RegionCode;

export function getResourceURL(resource: EndPoints.RESOURCE, params?: { [key: string]: string }, region?: string | null) {
    // Determine the API version for the given resource
    const apiVersion = resourceApiVersionMap[resource] || 'v1';

    // Get the base URL with the appropriate API version
    let BASE_URL: string = region
        ? ApplicationConfigManager.getInstance().getRegionAPIBaseUri(region, apiVersion)
        : (apiVersion === 'v2') ? ApplicationConfigManager.getInstance().getAPIBaseUriV2() :
            ApplicationConfigManager.getInstance().getAPIBaseUri()

    let BASE_V2_API_URL = `${configuration.v2ApiUrl}/v2`;


    switch (resource) {
        case EndPoints.RESOURCE.FIREWALL:
            return BASE_V2_API_URL + EndPoints.URI_FIREWALLS;
        case EndPoints.RESOURCE.REGIONS:
            return BASE_URL + EndPoints.URI_REGIONS_AZLIST;
        case EndPoints.RESOURCE.PERMISSION_POLICIES:
            return BASE_URL + EndPoints.URI_PERMISSION_POLICIES;
        case EndPoints.RESOURCE.XACCOUNT_ROLES:
            return BASE_URL + EndPoints.URI_XACCOUNT_ROLES;
        case EndPoints.RESOURCE.ACCOUNT:
            if (import.meta.env.VITE_MICROSUBSCRIPTIONS == "true") {
                return BASE_V2_API_URL + EndPoints.URI_LINK_ACCOUNTS;
            } else {
                return BASE_URL + EndPoints.URI_LINK_ACCOUNTS;
            }
        case EndPoints.RESOURCE.LOGPROFILE:
            return BASE_URL + EndPoints.LOGPROFILE;
        case EndPoints.RESOURCE.RULES:
            let url = BASE_URL + EndPoints.URI_RULES;
            if (params) {
                Object.keys(params).forEach(key => {
                    url = url.replace(`{${key}}`, params[key]);
                });
            }
            return url;
        case EndPoints.RESOURCE.LINKS:
            return BASE_URL + EndPoints.URI_INTEGRATIONS;
        case EndPoints.RESOURCE.RULESTACK:
            return BASE_URL + EndPoints.URI_RULESTACKS;
        case EndPoints.RESOURCE.LINKASSOCIATE:
            return BASE_URL + EndPoints.URI_LINK.replace("{firewall_id}", params?.firewall_id || "");
        case EndPoints.RESOURCE.RULESTACKASSOCIATE:
            return BASE_URL + EndPoints.URI_RULESTACK.replace("{firewall_id}", params?.firewall_id || "");
        case EndPoints.RESOURCE.SETTINGS:
            return BASE_V2_API_URL + EndPoints.URI_SETTINGS;
        case EndPoints.RESOURCE.SUBSCRIPTION:
            if (import.meta.env.VITE_MICROSUBSCRIPTIONS == "true") {
                return BASE_V2_API_URL + EndPoints.URI_SUBSCRIPTION;
            } else {
                return BASE_URL + EndPoints.URI_SUBSCRIPTION;
            }
        case EndPoints.RESOURCE.TENANT:
            return BASE_V2_API_URL + EndPoints.URI_TENANT;
        case EndPoints.RESOURCE.VALIDATE_SCM_CODE:
            return BASE_URL + EndPoints.URI_VALIDATE_SCM_CODE;
        case EndPoints.RESOURCE.SCM_BILLING_ASSOCIATION:
            return BASE_URL + EndPoints.URI_SCM_BILLING_ASSOCIATION;
        case EndPoints.RESOURCE.SUPPORT:
            return BASE_URL + EndPoints.URI_SUPPORT;
        case EndPoints.RESOURCE.TOKEN:
            return BASE_URL + EndPoints.URI_TOKEN;
        case EndPoints.RESOURCE.USER_FEDERATION:
            return BASE_URL + EndPoints.URI_USER_FEDERATION;
        case EndPoints.RESOURCE.USER:
            return BASE_URL + EndPoints.URI_USERS;
        case EndPoints.RESOURCE.NETWORK_MONITORING:
            return BASE_V2_API_URL + EndPoints.URI_NETWORK_MONITORING
        case EndPoints.RESOURCE.CLOUDMANAGER:
            return BASE_URL + EndPoints.URI_CLOUDMANAGER;
        default:
            throw new Error("Unsupported resource type");
    }
}

export const API = {
    FETCH_USERS: (request: ListUserRoleMappingRequest) => {
        return `${getResourceURL(EndPoints.RESOURCE.USER)}?${payloadToLowercaseParams(request)}`;
    },
    CREATE_USER: () => {
        return `${getResourceURL(EndPoints.RESOURCE.USER)}`;
    },
    UPDATE_USER: (request: UpdateUserRoleMappingRequest) => {
        return `${getResourceURL(EndPoints.RESOURCE.USER)}/${request.UserName}`;
    },
    DELETE_USER: (request: DeleteUserRoleMappingRequest) => {
        return `${getResourceURL(EndPoints.RESOURCE.USER)}/${request.UserName}`;
    },
    FETCH_FIREWALLS: (request: ListFirewallRequest) => {
        return `${getResourceURL(EndPoints.RESOURCE.FIREWALL)}?${payloadToLowercaseParams(request)}&mpregion=${MP_REGION()}`;
    },
    FETCH_FIREWALL_BY_ID: (fwid: string, region: string) => {
        //TODO: Fix the api endpoint once the cross region is fixed.
        // return `${getResourceURL(EndPoints.RESOURCE.FIREWALL)}/{fwid}`;
        if (!fwid || typeof fwid !== 'string') {
            throw new Error("Invalid firewall ID");
        }
        if (!region || typeof region !== 'string') {
            throw new Error("Invalid region");
        }
        return `${getResourceURL(EndPoints.RESOURCE.FIREWALL)}/${encodeURIComponent(fwid)}?region=${encodeURIComponent(region)}&mpregion=${MP_REGION()}`;
    },
    CREATE_FIREWALL: (region: string) => {
        return `${getResourceURL(EndPoints.RESOURCE.FIREWALL)}?region=${encodeURIComponent(region)}&mpregion=${MP_REGION()}`;
    },
    UPDATE_FIREWALL: (payload: UpdateFirewallRequest) => {
        if (!payload || !payload?.FirewallId) {
            throw new Error("Invalid firewall ID");
        }
        if (!payload || !payload.Region) {
            throw new Error("Invalid region");
        }
        return `${configuration.v2ApiUrl}/v2/config/ngfirewalls/${encodeURIComponent(payload.FirewallId)}?region=${encodeURIComponent(payload.Region)}&mpregion=${MP_REGION()}`;

    },
    FETCH_REGIONS: (payload: any | undefined) => {
        let resourceUrl = getResourceURL(EndPoints.RESOURCE.REGIONS);
        if (payload && payload.region) {
            resourceUrl += `?region=${payload.region}`;
        }
        return resourceUrl;
    },
    FETCH_PERMISSION_POLICIES: () => {
        return getResourceURL(EndPoints.RESOURCE.PERMISSION_POLICIES);
    },
    FETCH_XACCOUNT_ROLES: () => {
        return getResourceURL(EndPoints.RESOURCE.XACCOUNT_ROLES);
    },
    FETCH_XACCOUNT_ROLES_DETAILS: (payload: ListXAccountRequest | undefined) => {
        let resourceUrl = `${getResourceURL(EndPoints.RESOURCE.XACCOUNT_ROLES, {}, payload?.region)}/${payload?.accountId}?describeaccount=${payload?.describe}`;
        if (payload && payload.vpcId) {
            resourceUrl += '&vpcid=' + payload.vpcId;
        }
        return resourceUrl;
    },
    FETCH_RULES: (payload: ListRulesRequest) => {
        const url = getResourceURL(EndPoints.RESOURCE.RULES, {
            ruleStackName: payload.RuleStackName,
            ruleListName: payload.RuleListName
        }, payload?.Region);
        const params: any = {
            maxresults: payload.MaxResults || 1000,
        };
        if (payload.NextToken) {
            params.nexttoken = payload.NextToken;
        }
        if (payload.Describe !== undefined) {
            params.describe = payload.Describe;
        }
        return {
            url,
            params,
        };
    },
    DELETE_FIREWALL: (payload: DeleteFirewallRequest) => {
        return `${configuration.v2ApiUrl}/v2/config/ngfirewalls/${encodeURIComponent(payload.FirewallId)}?region=${encodeURIComponent(payload.Region)}`;

    },
    CREATE_LINK_ACCOUNT: (payload: CreateLinkAccountRequest | undefined) => {
        if (import.meta.env.VITE_MICROSUBSCRIPTIONS == "true") {
            return `${getResourceURL(EndPoints.RESOURCE.ACCOUNT, {})}?mpregion=${MP_REGION()}`; // pass MP region for V2 API
        } else {
            return getResourceURL(EndPoints.RESOURCE.ACCOUNT, {}); // default to V1 API
        }
    },
    FETCH_LINK_ACCOUNTS: (payload: ListLinkAccountRequest | undefined) => {
        let resourceUrl = getResourceURL(EndPoints.RESOURCE.ACCOUNT, {}, payload?.region);

        if (payload && payload.nexttoken) {
            resourceUrl = resourceUrl + `?maxresults=${payload?.maxresults || 25}&nexttoken=${payload?.nexttoken}`;
        } else {
            resourceUrl = resourceUrl + `?maxresults=${payload?.maxresults || 25}`;
        }
        if (payload?.describe) {
            resourceUrl = resourceUrl + `&describe=true`;
        }
        if (import.meta.env.VITE_MICROSUBSCRIPTIONS == "true") {
            return resourceUrl + `&mpregion=${MP_REGION()}`; // pass MP region for V2 API
        } else {
            return resourceUrl; // default to V1 API
        }
    },
    FETCH_LOG_PROFILE: (payload: DescribeLogProfileRequest | undefined) => {
        if (!payload || !payload?.FirewallId) {
            throw new Error("Invalid firewall ID");
        }
        if (!payload || !payload.Region) {
            throw new Error("Invalid region");
        }
        return `${configuration.v2ApiUrl}/v2/config/ngfirewalls/${encodeURIComponent(payload.FirewallId)}/logprofile?region=${encodeURIComponent(payload.Region)}`;
    },
    UPDATE_LOG_PROFILE: (payload: UpdateLogProfileRequest) => {
        if (!payload || !payload?.FirewallId) {
            buildError(null, new Error("Invalid firewall ID"));
        }
        if (!payload || !payload?.Region) {
            buildError(null, new Error("Invalid region"));
        }
        return `${configuration.v2ApiUrl}/v2/config/ngfirewalls/${encodeURIComponent(payload.FirewallId)}/logprofile?region=${encodeURIComponent(payload.Region)}`;
    },
    FETCH_RULESTACKS: (payload: ListRuleStacksRequest) => {
        return `${getResourceURL(EndPoints.RESOURCE.RULESTACK, {}, payload?.Region)}?candidate=${payload?.Candidate}&running=${payload?.Running}&scope=${payload?.Scope}`
    },
    FETCH_LINKS: () => getResourceURL(EndPoints.RESOURCE.LINKS, {}, ApplicationConfigManager.getInstance().getSupportedRegions().find(v => v.RegionCode === "us-east-1")?.RegionCode || ApplicationConfigManager.getInstance().getSupportedRegions()[0]?.RegionCode),
    FETCH_CLOUDMANAGER: () => `${getResourceURL(EndPoints.RESOURCE.CLOUDMANAGER, {}, ApplicationConfigManager.getInstance().getSupportedRegions().find(v => v.RegionCode === "us-east-1")?.RegionCode || ApplicationConfigManager.getInstance().getSupportedRegions()[0]?.RegionCode)}?mpregion=${MP_REGION()}`,
    ASSOCIATE_LINK: (firewall_id: string) => {
        if (!firewall_id || typeof firewall_id !== 'string') {
            throw new Error("Invalid firewall ID");
        }
        return `${configuration.v2ApiUrl}/v2/config/ngfirewalls/${encodeURIComponent(firewall_id)}/link`;
    },
    DISASSOCIATE_LINK: (firewall_id: string) => {
        if (!firewall_id || typeof firewall_id !== 'string') {
            throw new Error("Invalid firewall ID");
        }
        return `${configuration.v2ApiUrl}/v2/config/ngfirewalls/${encodeURIComponent(firewall_id)}/link`;
    },
    ASSOCIATE_RULESTACK: (firewall_id: string) => {
        if (!firewall_id || typeof firewall_id !== 'string') {
            throw new Error("Invalid firewall ID");
        }
        return `${configuration.v2ApiUrl}/v2/config/ngfirewalls/${encodeURIComponent(firewall_id)}/rulestack`;
    },
    DISASSOCIATE_RULESTACK: (firewall_id: string) => {
        if (!firewall_id || typeof firewall_id !== 'string') {
            throw new Error("Invalid firewall ID");
        }
        return `${configuration.v2ApiUrl}/v2/config/ngfirewalls/${encodeURIComponent(firewall_id)}/rulestack`;
    },
    FETCH_SETTINGS: (request: DescribeTenantSettingsRequest | undefined) => {
        //TODO remove the replace string method when the GET method for settings API is also moved to v2
        let baseUrl = getResourceURL(EndPoints.RESOURCE.SETTINGS).replace(/\/v2\//g, '/v1/');

        // Destructure to exclude Region from request parameters
        if (request) {
            const { Region, ...requestParams } = request;
            if (!isEmpty(requestParams)) {
                const params = {
                    ...(requestParams || {}),
                    //TODO - add back when settings GET migrates to v2
                    //mpregion: request?.Region || MP_REGION()
                };
                if (!isEmpty(params)) {
                    baseUrl += `?${payloadToLowercaseParams(params)}`;
                }
            }
        }
        return baseUrl;
    },
    BYOIP_SETTINGS: (region: string) => {
        let baseUrl = getResourceURL(EndPoints.RESOURCE.SETTINGS);

        const params = {
            Region: region,
            mpregion: MP_REGION()
        };

        baseUrl += `?${payloadToLowercaseParams(params)}`;
        return baseUrl;
    },
    UPDATE_SETTINGS: (request?: UpdateTenantSettingsRequest | undefined) => {
        //TODO remove the replace string method when the PUT method for settings API is also moved to v2
        let baseUrl = getResourceURL(EndPoints.RESOURCE.SETTINGS).replace(/\/v2\//g, '/v1/');
        return baseUrl;
    },
    CREATE_SUBSCRIPTION: () => {
        if (import.meta.env.VITE_MICROSUBSCRIPTIONS == "true") {
            return `${getResourceURL(EndPoints.RESOURCE.SUBSCRIPTION)}?mpregion=${MP_REGION()}`;
        } else {
            return `${getResourceURL(EndPoints.RESOURCE.SUBSCRIPTION)}`;
        }
    },
    CREATE_TENANT: () => {
        return `${getResourceURL(EndPoints.RESOURCE.TENANT)}?mpregion=${MP_REGION()}`;
    },
    FETCH_SUBSCRIPTIONS: (request: ListSubscriptionRequest) => {
        if (import.meta.env.VITE_MICROSUBSCRIPTIONS == "true") {
            return `${getResourceURL(EndPoints.RESOURCE.SUBSCRIPTION)}?${payloadToLowercaseParams(request)}&mpregion=${MP_REGION()}`; // pass MP region for V2 API
        } else {
            return `${getResourceURL(EndPoints.RESOURCE.SUBSCRIPTION)}?${payloadToLowercaseParams(request)}`; // default to V1 API
        }
    },
    VALIDATE_SCM_CODE: () => {
        return `${getResourceURL(EndPoints.RESOURCE.VALIDATE_SCM_CODE)}`;
    },
    ASSOCIATE_BILLING_ACCESS_CODE: () => {
        return `${getResourceURL(EndPoints.RESOURCE.SCM_BILLING_ASSOCIATION)}`;
    },
    FETCH_SUPPORT: (request: GetSupportRequest) => {
        return `${getResourceURL(EndPoints.RESOURCE.SUPPORT)}?${payloadToLowercaseParams(request)}`;
    },
    UPDATE_SUPPORT: (request: UpdateSupportRequest) => {
        return `${getResourceURL(EndPoints.RESOURCE.SUPPORT)}`;
    },
    FETCH_TOKENS: () => {
        return `${getResourceURL(EndPoints.RESOURCE.TOKEN)}`;
    },
    UPDATE_TOKENS: () => {
        return `${getResourceURL(EndPoints.RESOURCE.TOKEN)}`;
    },
    FETCH_USER_FEDERATION: (request: GetFederatedAccessRequest) => {
        return `${getResourceURL(EndPoints.RESOURCE.USER_FEDERATION)}?${payloadToLowercaseParams(request)}`;
    },
    UPDATE_USER_FEDERATION: () => {
        return `${getResourceURL(EndPoints.RESOURCE.USER_FEDERATION)}`;
    },
    FETCH_NETWORK_MONITORING: (request: DescribeNetworkMonitoringRequest) => {
        return `${getResourceURL(EndPoints.RESOURCE.NETWORK_MONITORING)}?${payloadToLowercaseParams(request)}&mpregion=${MP_REGION()}`;
    },
    UPDATE_NETWORK_MONITORING: (request: UpdateNetworkMonitoringRequest) => {
        return `${getResourceURL(EndPoints.RESOURCE.NETWORK_MONITORING)}?mpregion=${MP_REGION()}`;
    },
};
